import _ from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { HoverTooltip } from '@/core/HoverTooltip.atom';
import { IconWithSpinner } from '@/core/IconWithSpinner.atom';
import { sqWorkbookStore } from '@/core/core.stores';
import { onEnterKeypress, onEventPreventPropagation } from '@/core/onEnterKeypress.util';
import { DisplayDropdown } from '@/displays/DisplayDropdown.molecule';
import { navigateToTreeBuilderPage } from '@/main/routing.utilities';
import { fetchItemUsages } from '@/search/search.actions';
import { HierarchyNodeType } from '@/search/search.constants';
import { useSearchMode, useSearchSearching } from '@/search/search.store';
import { isItemTable, isScalingTable } from '@/search/search.utilities';
import { swapAsset as sqSearchUtilitiesSwapAsset, swapScalingTableRow } from '@/search/searchResult.utilities.service';
import { canWriteItem } from '@/services/authorization.service';
import { setActiveTool, setNonStoreItem } from '@/toolSelection/investigate.actions';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { loadDatafile } from '@/tools/importDatafile/importDatafile.actions';
import { doTrack } from '@/track/track.service';
import { errorToast } from '@/utilities/toast.utilities';
import {
  isAsset,
  isDatafile as isDatafileUtil,
  isDisplay as isDisplayUtil,
  isHierarchy,
  isHierarchyNode,
  isModel,
  isTableDefinition,
} from '@/utilities/utilities';
import { isViewOnlyWorkbookMode } from '@/utilities/workbookMode.utilities';
import { pinToWorkbook, unpinToWorkbook } from '@/workbook/workbook.actions';
import { WORKBOOK_DISPLAY } from '@/workbook/workbook.constants';
import { sqHierarchiesApi } from '@seeqdev/api/endpoint/HierarchiesApi';
import { SeeqNames } from '@seeqdev/constants';
import { Icon } from '@seeqdev/qomponents';
import type { SearchModes } from '@/search/search.constants';
import type { SelectedSearchItem } from '@/search/search.types';

export interface SearchResultIconsProps {
  item: SelectedSearchItem;
  pinned: SelectedSearchItem[];
  workbookDisplay: WORKBOOK_DISPLAY | undefined;
  workbookId: string;
  onItemClick: (item: SelectedSearchItem, mode: SearchModes) => unknown;
  allowAssetReplacement: boolean;
  restrictExploration?: boolean;
}

export const SearchResultIcons = (props: SearchResultIconsProps) => {
  const {
    item,
    pinned,
    workbookDisplay,
    workbookId,
    onItemClick,
    allowAssetReplacement,
    restrictExploration = false,
  } = props;

  const [isSwapping, setIsSwapping] = useState(false);
  const [isSwappingRow, setIsSwappingRow] = useState(false);
  const [isPinning, setIsPinning] = useState(false);
  const [isLoadingHierarchy, setIsLoadingHierarchy] = useState(false);

  const { t } = useTranslation();
  const showScalingTableSearchItems = item.ancestors?.some(
    (ancestor) => ancestor.type === SeeqNames.Types.TableDefinition,
  );
  const isHierarchyOutput = isHierarchy(item);
  const isHierarchyItem = isHierarchyNode(item);
  const hierarchyItemType = item.node?.itemMetadata?.type ?? item.type;
  const hierarchyItemTypeIsAsset = isHierarchyItem && hierarchyItemType === SeeqNames.Types.Asset;
  const hierarchyItemHasChildrenAndIsRealItem =
    isHierarchyItem && hierarchyItemType !== HierarchyNodeType && !item.node?.leaf;
  const itemIsAsset = isAsset(item) || hierarchyItemTypeIsAsset;
  const isNonAssetTreeItemWithChildren =
    hierarchyItemHasChildrenAndIsRealItem || (item?.hasActiveChildren && !isHierarchyItem);
  const isDatafile = isDatafileUtil(item);
  const isDisplay = isDisplayUtil(item);
  const isModelItem = isModel(item);
  const isTableDefinitionItem = isTableDefinition(item);
  const isScalingTableItem = isTableDefinitionItem && isScalingTable(item);
  const isScalingTableRowNode = item?.isTableRow === true;
  const isItemTableItem = isTableDefinitionItem && isItemTable(item);
  const canEditHierarchy =
    !isViewOnlyWorkbookMode() && (item.effectivePermissions ? canWriteItem(item) : !item.isLocked);
  const showInfoButton =
    itemIsAsset ||
    isDatafile ||
    isHierarchyOutput ||
    isNonAssetTreeItemWithChildren ||
    isScalingTableItem ||
    isItemTableItem;
  const showTrendIcon =
    (!itemIsAsset && !isDatafile && !isDisplay && !isHierarchyItem && item?.hasActiveChildren) ||
    (hierarchyItemHasChildrenAndIsRealItem && !hierarchyItemTypeIsAsset);
  const showSwapIcon = isAsset(item) || isScalingTableRowNode;
  const searchMode = useSearchMode();
  const searchSearching = useSearchSearching();
  const isLoadingUsages = Boolean(searchSearching);
  const isSearchingUsages = searchMode === 'usages';
  const isPinned = _.chain(pinned).map('id').includes(item.id).value();
  const isEdit = workbookDisplay === WORKBOOK_DISPLAY.EDIT;
  const displayParentProperties = () => {
    setActiveTool(TREND_TOOLS.PROPERTIES);
    setNonStoreItem(item);
  };

  const swapAsset = () => {
    doTrack('Search results', 'Swap Assets');
    setIsSwapping(true);

    sqSearchUtilitiesSwapAsset(item)
      .catch((error) => errorToast({ httpResponseOrError: error, displayForbidden: true }))
      .finally(() => setIsSwapping(false));
  };

  const swapRow = () => {
    doTrack('Search results', 'Swap Scaling Table Row');
    setIsSwappingRow(true);

    swapScalingTableRow(item).finally(() => setIsSwappingRow(false));
  };

  const togglePin = () => {
    doTrack('Search results', 'pin to Workbook');
    const pinFunction = isPinned ? unpinToWorkbook : pinToWorkbook;
    setIsPinning(true);
    return pinFunction(workbookId, item.id).finally(() => setIsPinning(false));
  };

  const findUsages = () => {
    doTrack('Analysis', 'Usages', 'from search');
    return fetchItemUsages(item, 'main', sqWorkbookStore.workbookId ? [sqWorkbookStore.workbookId] : [], false);
  };

  const editHierarchy = async (event: React.MouseEvent | React.KeyboardEvent | undefined) => {
    try {
      navigateToTreeBuilderPage({
        tableId:
          item.tableDefinition?.id ?? (await sqHierarchiesApi.getHierarchy({ id: item.id })).data.tableDefinition.id,
        hierarchyId: item.id,
        event,
      });
    } finally {
      setIsLoadingHierarchy(false);
    }
  };

  const getInfoTooltipText = () => {
    if (isDatafile) return t('PROPERTIES.DATAFILE');
    if (isTableDefinitionItem) return t('PROPERTIES.TABLE');
    return t('PROPERTIES.ASSET');
  };

  return !showScalingTableSearchItems ? (
    <div data-testid="searchResultIcons" className="flexColumnContainer tw:items-center">
      {showInfoButton && (
        <span onClick={onEventPreventPropagation(displayParentProperties)}>
          <Icon icon="fa-info-circle" tooltip={getInfoTooltipText()} extraClassNames="searchBtn" testId="infoIcon" />
        </span>
      )}

      {showTrendIcon && (
        <HoverTooltip text="SEARCH_DATA.TREND_ITEM" placement="top">
          <span
            onClick={onEventPreventPropagation(() => isSwapping || onItemClick(item, searchMode))}
            onKeyUp={onEnterKeypress(() => isSwapping || onItemClick(item, searchMode))}>
            <IconWithSpinner
              icon="fa-chart-area"
              extraClassNames="searchBtn"
              spinning={isSwapping}
              testId="trendIcon"
            />
          </span>
        </HoverTooltip>
      )}

      {isDatafile && (
        <span
          onClick={onEventPreventPropagation(() => loadDatafile(item.id))}
          onKeyUp={onEnterKeypress(() => loadDatafile(item.id))}>
          <Icon
            icon="fa-pencil"
            tooltip={t('SEARCH_DATA.EDIT_CSV')}
            extraClassNames="searchBtn"
            testId="editDatafileIcon"
          />
        </span>
      )}

      {isHierarchyOutput && (
        <HoverTooltip text={canEditHierarchy ? 'SEARCH_DATA.EDIT_HIERARCHY' : 'SEARCH_DATA.VIEW_HIERARCHY'}>
          <span
            onClick={onEventPreventPropagation((event: React.MouseEvent | React.KeyboardEvent | undefined) =>
              editHierarchy(event),
            )}>
            <IconWithSpinner
              icon={canEditHierarchy ? 'fa-pencil' : 'fa-eye'}
              extraClassNames="searchBtn"
              testId={canEditHierarchy ? 'editHierarchyIcon' : 'viewHierarchyIcon'}
              spinning={isLoadingHierarchy}
            />
          </span>
        </HoverTooltip>
      )}

      {isDisplay && (
        <DisplayDropdown
          item={item}
          isPinning={isPinning}
          isPinned={isPinned}
          togglePin={togglePin}
          restrictExploration={restrictExploration}
        />
      )}

      {isSearchingUsages && (
        <HoverTooltip text="SEARCH_DATA.USAGES">
          <span onClick={onEventPreventPropagation(() => findUsages())} onKeyUp={onEnterKeypress(() => findUsages())}>
            <IconWithSpinner
              icon="fc-usage"
              extraClassNames="searchBtn"
              testId="usagesIcon"
              spinning={isLoadingUsages}
            />
          </span>
        </HoverTooltip>
      )}

      {allowAssetReplacement && showSwapIcon && (
        <HoverTooltip text="ASSET_REPLACE.TOOLTIP" placement="bottom">
          <span
            onClick={onEventPreventPropagation(() => !isSwapping && swapAsset())}
            onKeyUp={onEnterKeypress(() => !isSwapping && swapAsset())}>
            <IconWithSpinner
              icon="fa-arrow-right-arrow-left"
              extraClassNames="searchBtn"
              spinning={isSwapping}
              testId="assetSwapIcon"
            />
          </span>
        </HoverTooltip>
      )}

      {isEdit && !isModelItem && !isSearchingUsages && !isDisplay && !isHierarchyItem && (
        <HoverTooltip text={isPinned ? 'SEARCH_DATA.UNPIN' : 'SEARCH_DATA.PIN'}>
          <span
            onClick={onEventPreventPropagation(() => !isPinning && togglePin())}
            onKeyUp={onEnterKeypress(() => !isPinning && togglePin())}>
            <IconWithSpinner
              icon={isPinned ? 'fc-pin' : 'fc-pin-o'}
              extraClassNames="searchBtn"
              testId="pinIcon"
              spinning={isPinning}
            />
          </span>
        </HoverTooltip>
      )}
    </div>
  ) : (
    <div data-testid="scalingTableItemIcons">
      {allowAssetReplacement && isScalingTableRowNode && (
        <HoverTooltip text="SCALING_ROW_REPLACE.TOOLTIP" placement="bottom">
          <span
            onClick={onEventPreventPropagation(() => !isSwappingRow && swapRow())}
            onKeyUp={onEnterKeypress(() => !isSwappingRow && swapRow())}>
            <IconWithSpinner
              icon="fa-arrow-right-arrow-left"
              extraClassNames="searchBtn"
              spinning={isSwappingRow}
              testId="rowSwapIcon"
            />
          </span>
        </HoverTooltip>
      )}
    </div>
  );
};
